import type { MtfDetail } from "@/lib/forex-mtf-api";
import { formatRunTime, stageReasonLabel, resolveDisplayReason } from "./mtf-display";
import { readableDecisionReason } from "./decision-reason";
import { candidateDirection, hasPostStrategyEvidence, isStrategyStage, stoppedAtStrategy, strategyReason } from "./mtf-display-stages";

type Stage = MtfDetail["stages"][number];

export interface StageSummary {
  conclusion: string;
  facts: Array<{ label: string; value: string }>;
}

const direction = (value: unknown): string | null =>
  value === "BUY" ? "买入" : value === "SELL" ? "卖出" : value === "HOLD" ? "继续观察" : null;

const object = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};

const reason = (stage: Stage): string =>
  stageReasonLabel(stage.reason_code) || "尚无记录";

export function stageSummary(detail: MtfDetail, stage: Stage): StageSummary {
  const empty = { conclusion: "前序阶段尚未完成，本阶段尚无记录。", facts: [] };
  if (isStrategyStage(stage)) {
    const candidate = detail.strategy_candidate;
    if (!candidate) {
      const fastBlocked = detail.stages.some(item => item.stage === "fast_gate" && ["blocked", "failed", "no_signal", "hold"].includes(item.status));
      return {conclusion: stage.status === "not_started" && fastBlocked
        ? "快速市场检查未通过，本轮未进入策略检查。"
        : stage.status === "unknown" ? "未记录策略检查证据。" : resolveDisplayReason(stage.reason_code), facts: []};
    }
    const evaluation = object(candidate.evaluation);
    const evidence = object(evaluation.evidence);
    const side = candidateDirection(detail);
    const facts: StageSummary["facts"] = [];
    if (side) facts.push({label: candidate.status === "CANDIDATE" ? "候选方向" : "准备方向", value: side});
    if (typeof evidence.armed_at === "number" && Number.isFinite(evidence.armed_at)) facts.push({label: "形态准备时间", value: formatRunTime(evidence.armed_at)});
    if (typeof evaluation.as_of === "number" && Number.isFinite(evaluation.as_of)) facts.push({label: "检查截至", value: formatRunTime(evaluation.as_of)});
    const setup = candidate.setup_id || evaluation.setup_id;
    if (typeof setup === "string") facts.push({label: "形态编号", value: setup});
    const indicators = object(object(detail.features).indicator_roles);
    for (const [role, raw] of Object.entries(indicators)) {
      const indicator = object(raw);
      for (const [label, value] of [["冻结收盘", indicator.close], ["EMA", object(indicator.ema).value], ["ATR", object(indicator.atr).value]]) {
        if (typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value) && Number.isFinite(Number(value))) {
          facts.push({label: `${indicator.timeframe || role} ${label}`, value: Number(value).toLocaleString("en-US", {useGrouping: false, maximumFractionDigits: 6})});
        }
      }
    }
    const checks = detail.explanation?.stage === "strategy_candidate" ? detail.explanation.details.checks : undefined;
    if (checks?.length) {
      for (const check of checks) facts.push({label: check.code, value: `${check.passed ? "通过" : "未通过"} · 实际 ${check.actual} · 要求 ${check.expected}`});
    } else facts.push({label: "条件明细", value: "本轮未保存逐项条件明细"});
    const code = strategyReason(detail);
    const summary = resolveDisplayReason(code, detail.explanation);
    const conflict = ['HOLD', 'NO_CANDIDATE'].includes(candidate.status) && hasPostStrategyEvidence(detail);
    return {conclusion: conflict ? `${summary}；同时存在后续阶段记录，需核对证据一致性。` : summary, facts};
  }

  if (stage.status === "not_started") {
    if (stoppedAtStrategy(detail) && ["full_decision", "admission", "master_signal", "master_execution", "master_fill", "follower_result_summary"].includes(stage.stage)) {
      return {conclusion: "上游策略检查未形成交易候选，本阶段未启动。", facts: []};
    }
    if (stage.stage === "full_decision" && hasPostStrategyEvidence(detail)) {
      return {conclusion: "已有后续判断或执行记录，调用或结果状态需核对，不能确认为未调用。", facts: []};
    }
    const pending: Record<string, string> = {
      master_signal: "尚未生成共享策略信号。",
      master_execution: "平台账号尚无执行命令。",
      master_fill: "平台账号尚无成交回报。",
      follower_handoff: "账号独立执行，不存在主账号成交移交。",
      follower_result_summary: "参与账号尚无可确认的执行结果。",
    };
    return { ...empty, conclusion: pending[stage.stage] || stageReasonLabel(stage.reason_code) || empty.conclusion };
  }

  switch (stage.stage) {
    case "trigger_received":
      return {
        conclusion: "系统已收到自然周期收盘触发，开始本轮判断。",
        facts: [{ label: "运行开始", value: formatRunTime(detail.run.created_at) }],
      };
    case "input_frozen": {
      const inputMap = object(detail.input_summary);
      const roles = object(detail.timeframe_roles);
      const inputs = [...new Set([...Object.keys(inputMap), ...Object.keys(roles)])].map(key => object(inputMap[key]));
      const ready = inputs.length > 0 && inputs.every((item) => ["ready", "completed"].includes(String(item.status)));
      const timeframes = [...new Set([...Object.values(roles), ...inputs.map(item => item.timeframe)].filter(value => typeof value === "string"))];
      return {
        conclusion: ready ? "本轮需要的行情输入已备齐。" : "部分行情输入尚未备齐或需要核对。",
        facts: [
          ...(timeframes.length ? [{label: "本轮周期", value: timeframes.join("、")}] : []),
          ...(inputs.length ? [{ label: "已备齐数据角色", value: `${inputs.filter((item) => ["ready", "completed"].includes(String(item.status))).length} / ${inputs.length}` }] : []),
        ],
      };
    }
    case "fast_gate": {
      const fast = object(detail.fast);
      const candidate = fast.status === "CANDIDATE";
      const side = direction(fast.candidate_direction);
      return {
        conclusion: candidate && !side ? "行情和市场条件已通过初筛，候选结果见策略检查。" : candidate ? "快速判断已形成交易候选。" : reason(stage),
        facts: candidate && side ? [{ label: "候选方向", value: side }] : [],
      };
    }
    case "full_decision": {
      const full = object(detail.full);
      const completed = ["completed", "hold"].includes(stage.status);
      const judgment = detail.master_judgment?.status === "completed" ? detail.master_judgment : null;
      const action = direction(judgment?.action ?? full.action);
      const rawThesis = judgment?.summary ?? full.thesis;
      const thesis = typeof rawThesis === "string" ? rawThesis.trim() : "";
      const attempts = detail.full_attempts?.length || 0;
      return {
        conclusion: completed && action ? `主决策判断：${action}。` : reason(stage),
        facts: completed && action && thesis ? [{ label: "判断依据", value: /[\u3400-\u9fff]/u.test(thesis) ? readableDecisionReason(thesis) : "历史原因原文见上方 Master 判断详情" }] : attempts ? [{ label: "判断尝试", value: `${attempts} 次，尚无可用结论` }] : [],
      };
    }
    case "admission": {
      const admission = object(detail.admission);
      const status = String(admission.status || "").toUpperCase();
      return {
        conclusion: ["APPROVED", "PASSED", "ADMITTED"].includes(status) ? "决策结果准入校验已通过。" : reason(stage),
        facts: [],
      };
    }
    case "master_signal": {
      const lineage = object(detail.execution_lineage);
      const signal = object(lineage.signal_summary);
      const side = direction(signal.action);
      return {
        conclusion: signal.signal_id || stage.status === "completed" ? "已生成共享策略信号；这不代表任何账号已经下单或成交。" : "尚未生成共享策略信号。",
        facts: side ? [{ label: "信号方向", value: side }] : [],
      };
    }
    case "master_execution":
      return {
        conclusion: stage.status === "completed" ? "平台账号执行步骤已完成；成交以本账号回报为准。"
          : ["processing", "running", "pending"].includes(stage.status) ? "平台账号执行命令处理中，尚未确认成交。"
          : stage.status === "blocked" || stage.status === "failed" ? reason(stage)
          : "平台账号尚无执行命令。",
        facts: [],
      };
    case "master_fill": {
      const occurredAt = object(stage.details).occurred_at;
      return {
        conclusion: stage.status === "completed" ? "平台账号成交事实已确认。" : "尚未确认平台账号成交。",
        facts: stage.status === "completed" && typeof occurredAt === "number" ? [{ label: "成交确认时间", value: formatRunTime(occurredAt) }] : [],
      };
    }
    case "follower_handoff":
      return { conclusion: "账号独立执行，不存在主账号成交移交。", facts: [] };
    case "follower_result_summary": {
      const accounts = object(detail.execution_lineage).accounts;
      const followers = Array.isArray(accounts) ? accounts.filter((item) => object(item).target_role === "user_follower") : [];
      return {
        conclusion: followers.length ? "参与账号独立执行；逐账号成交以各自 EA 与 Broker 回报为准。" : "参与账号尚无可确认的执行结果。",
        facts: followers.length ? [{ label: "参与账号", value: `${followers.length} 个` }] : [],
      };
    }
    default:
      return { conclusion: "此阶段暂无可读摘要，请凭运行编号排查。", facts: [] };
  }
}
